import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { FilterTemplate } from '@repo/shared/dtos/beneficiary'
import { isEqual } from 'lodash'

import { storeActions } from '@/store/actions'
import { getWidgetData } from '@/store/dashboard-widgets/thunks'
import { DashboardWidget } from '@/store/dashboard-widgets/types'

export const defaultWidget: DashboardWidget = {
  filterTemplate: {
    id: 'default',
    name: 'defaultWidgetName',
    filters: [],
    createdAt: new Date().getTime(),
    updatedAt: new Date().getTime(),
  },
  loading: true,
  shortList: [],
  totalItems: 0,
} satisfies DashboardWidget

interface State {
  widgets: DashboardWidget[]
}

const initialState: State = {
  widgets: [],
}

const slice = createSlice({
  name: 'dashboardWidgets',
  initialState,
  reducers: {
    setWidgets(state, action: PayloadAction<FilterTemplate[]>) {
      const widgets: DashboardWidget[] = []
      action.payload.forEach((filterTemplate) => {
        const widget = getWidget({ state, filterTemplate })
        if (!widget) return
        widgets.push(widget)
      })
      state.widgets = widgets
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      storeActions.initOrganization.pending,
      (
        _,
        {
          meta: {
            arg: { isSameOrganization },
          },
        },
      ) => {
        if (isSameOrganization) return
        return initialState
      },
    )

    builder
      .addCase(
        getWidgetData.pending,
        (
          state,
          {
            meta: {
              arg: { widget },
            },
          },
        ) => {
          updateWidget({ fields: { loading: true }, id: widget.filterTemplate.id, state })
        },
      )
      .addCase(getWidgetData.fulfilled, (state, action) => {
        updateWidget({
          fields: {
            lastUpdatedAt: new Date().getTime(),
            loading: false,
            shortList: action.payload.shortList,
            totalItems: action.payload.totalItems,
          },
          id: action.meta.arg.widget.filterTemplate.id,
          state,
        })
      })
      .addCase(getWidgetData.rejected, (state, { meta: { arg } }) => {
        updateWidget({ fields: { loading: false }, id: arg.widget.filterTemplate.id, state })
      })
  },
})

function updateWidget({
  state,
  fields,
  id,
}: {
  state: State
  fields: Partial<DashboardWidget>
  id: FilterTemplate['id']
}) {
  const widget = state.widgets.find((w) => w.filterTemplate.id === id)
  if (!widget) return
  Object.assign(widget, { ...fields })
}

function getWidget({
  state,
  filterTemplate,
}: {
  state: State
  filterTemplate: FilterTemplate
}): DashboardWidget | undefined {
  const foundWidget = state.widgets.find((w) => w.filterTemplate.id === filterTemplate.id)
  if (
    foundWidget &&
    foundWidget.filterTemplate.name === filterTemplate.name &&
    isEqual(foundWidget.filterTemplate.filters, filterTemplate.filters)
  )
    return foundWidget
  return {
    loading: true,
    shortList: [],
    filterTemplate,
    totalItems: 0,
  }
}

export const { setWidgets } = slice.actions
export type { State as DashboardWidgetsState }
export const dashboardWidgetsInitialState = initialState
export default slice.reducer
